import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  CssBaseline,
  hexToRgb,
  LinearProgress,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import SurveyFooter from '../../components/SurveyFooter/SurveyFooter';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import SurveyHeader from '../../components/SurveyHeader/SurveyHeader';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import CustomSurveyContent from '../../components/CustomSurveyContent/CustomSurveyContent';
import { LocalTranslationContext, useLocalTranslation } from '../../hooks/useLocalTranslation';
import i18n from 'i18next';
import {
  scopeSurveyCustomCss,
  SURVEY_CUSTOM_CSS_SCOPE_CLASS,
  SURVEY_CUSTOM_CSS_SCOPE_SELECTOR,
} from '../../shared/SurveyCustomCss';

interface Props {
  previewSurvey?: ISurvey;
}

const CustomSurveyPage: FC<Props> = ({ previewSurvey }) => {
  const { t } = useTranslation('customSurvey');
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();

  const [customSurveyPage, setCustomSurveyPage] = useState<(ISurvey & Record<string, any>) | null>(
    previewSurvey ?? null
  );

  const [currentLanguage, setCurrentLanguage] = useState<string>('de-DE');
  const localTranslation = useLocalTranslation({ lang: currentLanguage.slice(0, 2) });

  const [error, setError] = useState<boolean>(false);
  const [currentId, setCurrentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (previewSurvey) {
      setCustomSurveyPage(previewSurvey);
      const lang =
        previewSurvey.lang === 'en'
          ? 'en-GB'
          : previewSurvey.lang === 'de'
          ? 'de-DE'
          : previewSurvey.lang;
      setCurrentLanguage(lang);
      return;
    }
    const controller = new AbortController();
    const splitUrl = window.location.href.split('/');
    const extractValue = splitUrl[splitUrl.length - 1].toString();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurveyPublic(
          extractValue,
          'customsurvey',
          setLoading,
          controller.signal
        );
        if (response) {
          setCurrentId(response.id);
          setCustomSurveyPage(response);
          if (response.language) {
            i18n.changeLanguage(response.language);
          }
          if (response.lang) {
            if (response.lang === 'en') {
              response.lang = 'en-GB';
            } else if (response.lang === 'de') {
              response.lang = 'de-DE';
            }
            setCurrentLanguage(response.lang);
          }
        } else {
          setError(true);
        }
      } catch (error) {
        setError(true);
        console.error('Fehler beim Abrufen der Umfrage:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, [currentId, previewSurvey]);

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = customSurveyPage?.label
      ? customSurveyPage?.label
      : `${t('app_branding')} ${t('survey')}`;
  }, [setCurrentView, t, customSurveyPage]);

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const effectiveFontFamily =
    customSurveyPage?.customFont && customSurveyPage.customFontFormat
      ? `"${customSurveyPage.id}"`
      : '"Arial", "Helvetica", sans-serif';

  const customTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: customSurveyPage?.primaryColor
              ? customSurveyPage.primaryColor
              : theme.palette.primary.main,
          },
          divider: customSurveyPage?.secondaryColor
            ? alpha(hexToRgb(customSurveyPage.secondaryColor), 0.2)
            : theme.palette.divider,
        },
        typography: {
          fontFamily: effectiveFontFamily,
        },
      }),
    [
      customSurveyPage?.primaryColor,
      customSurveyPage?.secondaryColor,
      effectiveFontFamily,
      theme.palette.divider,
      theme.palette.primary.main,
    ]
  );

  if (customSurveyPage) {
    const scopedCustomCss = scopeSurveyCustomCss(customSurveyPage.customCss);
    const customFontCss =
      customSurveyPage.customFont && customSurveyPage.customFontFormat
        ? `
            @font-face {
              font-family: "${customSurveyPage.id}";
              src: url(${customSurveyPage.customFont}) format("${getFontFormatByFileExtension(
            customSurveyPage.customFontFormat as FontFileExtensions
          )}");
            }

            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `
        : `
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `;

    return (
      <LocalTranslationContext.Provider value={localTranslation}>
        <CssBaseline />
        <Box
          className="survey-page-shell survey-page-shell--custom"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            backgroundColor: '#ffffff',
          }}
        >
          <ThemeProvider theme={customTheme}>
            <style>{customFontCss}</style>
            {scopedCustomCss && <style>{scopedCustomCss}</style>}
            {loading && <LinearProgress />}
            <Box
              className={SURVEY_CUSTOM_CSS_SCOPE_CLASS}
              sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <Box
                className="survey-page survey-page--custom custom-survey-page-root"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100%',
                  whiteSpace: 'pre-wrap',
                  flex: 1,
                  fontFamily: effectiveFontFamily,
                }}
              >
                <SurveyHeader
                  logoLink={customSurveyPage.logoLink}
                  logo={customSurveyPage.companyLogo}
                  selectedLanguage={currentLanguage}
                  setSelectedLanguage={setCurrentLanguage}
                  languageKeys={[
                    customSurveyPage.lang,
                    ...Object.keys(customSurveyPage?.i18n ?? {}),
                  ]}
                />
                <CustomSurveyContent
                  {...customSurveyPage}
                  selectedLanguage={currentLanguage}
                />
              </Box>
            </Box>
          </ThemeProvider>

          <SurveyFooter
            company={customSurveyPage.companyName}
            privacyPolicyLink={customSurveyPage.privacyPolicyLink}
            imprintLink={customSurveyPage.imprintLink}
          />
        </Box>
      </LocalTranslationContext.Provider>
    );
  } else {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {error ? (
            <Typography>{t('survey_not_found_or_archived')}</Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Section>
    );
  }
};

export default CustomSurveyPage;
